(function () {
  'use strict';

  const root = document.documentElement;
  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney
      ? window.Shopify.formatMoney(cents)
      : '$' + (cents / 100).toFixed(2));

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    const items = document.querySelectorAll('[data-sq-reveal]:not(.is-visible)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    items.forEach((el) => io.observe(el));
  }

  /* ---------- sticky header ---------- */
  function initHeader() {
    const header = document.querySelector('[data-sq-header]');
    if (!header) return;

    let last = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-stuck', y > 8);

      const openPanel = document.querySelector('.sq-drawer.is-open, .sq-search.is-open');
      if (!openPanel) {
        const goingDown = y > last && y > 240;
        header.classList.toggle('is-hidden', goingDown);
      }

      last = y;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ---------- announcement rotator ---------- */
  function initAnnouncement() {
    document.querySelectorAll('[data-sq-announce]').forEach((bar) => {
      const slides = Array.from(bar.querySelectorAll('.sq-announce__slide'));
      if (slides.length < 2) return;

      let index = 0;
      let timer = null;
      const delay = parseInt(bar.dataset.sqAnnounce, 10) || 5000;

      const show = (next) => {
        const from = slides[index];
        const to = slides[(next + slides.length) % slides.length];
        if (from === to) return;

        from.classList.remove('is-active');
        from.classList.add('is-leaving');
        setTimeout(() => from.classList.remove('is-leaving'), 600);

        to.classList.add('is-active');
        index = slides.indexOf(to);
      };

      const start = () => {
        stop();
        timer = setInterval(() => show(index + 1), delay);
      };
      const stop = () => timer && clearInterval(timer);

      bar.querySelector('[data-sq-announce-prev]')?.addEventListener('click', () => {
        show(index - 1);
        start();
      });
      bar.querySelector('[data-sq-announce-next]')?.addEventListener('click', () => {
        show(index + 1);
        start();
      });

      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      start();
    });
  }

  /* ---------- panels: drawers + search ---------- */
  const panels = {
    current: null,
    lastFocus: null,
  };

  function openPanel(id) {
    const panel = document.getElementById(id);
    if (!panel) return;

    if (panels.current && panels.current !== panel) closePanel();

    panels.lastFocus = document.activeElement;
    panel.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    document.querySelector('[data-sq-scrim]')?.classList.add('is-open');
    document.body.classList.add('sq-no-scroll');
    panels.current = panel;

    const focusable = panel.querySelector(
      'input:not([type=hidden]), button, a[href], [tabindex]:not([tabindex="-1"])'
    );
    setTimeout(() => focusable?.focus(), 260);

    if (id === 'sq-cart-drawer') refreshCartDrawer();
  }

  function closePanel() {
    if (!panels.current) return;
    panels.current.classList.remove('is-open');
    panels.current.setAttribute('aria-hidden', 'true');
    document.querySelector('[data-sq-scrim]')?.classList.remove('is-open');
    document.body.classList.remove('sq-no-scroll');
    panels.current = null;
    panels.lastFocus?.focus();
  }

  function initPanels() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-sq-open]');
      if (opener) {
        event.preventDefault();
        const id = opener.dataset.sqOpen;
        const panel = document.getElementById(id);
        if (panel && panel.classList.contains('is-open')) closePanel();
        else openPanel(id);
        return;
      }

      if (event.target.closest('[data-sq-close]') || event.target.closest('[data-sq-scrim]')) {
        event.preventDefault();
        closePanel();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanel();
      if (event.key !== 'Tab' || !panels.current) return;

      const nodes = Array.from(
        panels.current.querySelectorAll(
          'a[href], button:not(:disabled), input:not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;

      const first = nodes[0];
      const lastNode = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------- carousels ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-sq-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-sq-track]');
      if (!track) return;

      const prev = carousel.querySelector('[data-sq-prev]');
      const next = carousel.querySelector('[data-sq-next]');

      const step = () => {
        const card = track.firstElementChild;
        if (!card) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
        return (card.getBoundingClientRect().width + gap) * (window.innerWidth > 1000 ? 2 : 1);
      };

      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();

      let down = false;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        down = true;
        moved = 0;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      });

      track.addEventListener('pointermove', (event) => {
        if (!down) return;
        const delta = event.clientX - startX;
        moved = Math.abs(delta);
        if (moved > 4) track.classList.add('is-dragging');
        track.scrollLeft = startScroll - delta;
      });

      const release = () => {
        if (!down) return;
        down = false;
        setTimeout(() => track.classList.remove('is-dragging'), 40);
        sync();
      };

      track.addEventListener('pointerup', release);
      track.addEventListener('pointerleave', release);
      track.addEventListener('pointercancel', release);
    });
  }

  /* ---------- grid density ---------- */
  function initDensity() {
    document.querySelectorAll('[data-sq-density]').forEach((group) => {
      const grid = document.querySelector(group.dataset.sqDensity);
      if (!grid) return;

      group.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('button').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          grid.dataset.density = btn.dataset.value;
        });
      });
    });
  }

  /* ---------- product variants ---------- */
  function initVariantPickers() {
    document.querySelectorAll('[data-sq-variants]').forEach((form) => {
      let variants = [];
      try {
        variants = JSON.parse(form.querySelector('[data-sq-variant-data]')?.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!variants.length) return;

      const idInput = form.querySelector('[data-sq-variant-id]');
      const priceEl = form.querySelector('[data-sq-price]');
      const submit = form.querySelector('[data-sq-submit]');

      const apply = () => {
        const chosen = Array.from(form.querySelectorAll('.sq-option')).map(
          (opt) => opt.querySelector('.is-selected')?.dataset.value
        );

        const match = variants.find((variant) =>
          chosen.every((value, i) => value == null || variant.options[i] === value)
        );
        if (!match) return;

        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);
        if (submit) {
          submit.disabled = !match.available;
          submit.textContent = match.available ? 'Add to cart' : 'Sold out';
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      };

      form.querySelectorAll('.sq-option__value').forEach((btn) => {
        btn.addEventListener('click', () => {
          btn.parentElement
            .querySelectorAll('.sq-option__value')
            .forEach((sibling) => sibling.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          apply();
        });
      });
    });
  }

  /* ---------- cart ---------- */
  function setCartCount(count) {
    document.querySelectorAll('[data-sq-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  async function refreshCartDrawer() {
    const body = document.querySelector('[data-sq-cart-body]');
    const foot = document.querySelector('[data-sq-cart-foot]');
    if (!body) return;

    try {
      const cart = await fetch(`${root.dataset.sqRoot || '/'}cart.js`).then((r) => r.json());
      setCartCount(cart.item_count);

      if (!cart.item_count) {
        body.innerHTML =
          '<div class="sq-cart-empty"><p class="sq-h3">Your bag is empty</p>' +
          '<p class="sq-lede">Nothing here yet — go find your next quest.</p>' +
          `<a class="sq-btn" href="${root.dataset.sqRoot || '/'}collections/all">Start shopping</a></div>`;
        if (foot) foot.hidden = true;
        return;
      }

      body.innerHTML = cart.items
        .map(
          (item) => `
        <div class="sq-cart-line">
          <img src="${item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : ''}" alt="${
            item.product_title
          }" loading="lazy">
          <div>
            <a class="sq-cart-line__title" href="${item.url}">${item.product_title}</a>
            <p class="sq-cart-line__meta">${
              item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''
            }</p>
            <p class="sq-cart-line__meta">Qty ${item.quantity} · ${money(item.final_line_price)}</p>
          </div>
        </div>`
        )
        .join('');

      if (foot) {
        foot.hidden = false;
        const total = foot.querySelector('[data-sq-cart-total]');
        if (total) total.textContent = money(cart.total_price);
      }
    } catch (error) {
      body.innerHTML = '<p class="sq-lede">Could not load your bag. Please refresh.</p>';
    }
  }

  function initCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-sq-add-form]');
      if (!form) return;

      event.preventDefault();
      const button = form.querySelector('[type=submit]');
      const original = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }

      try {
        const response = await fetch(`${root.dataset.sqRoot || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('add failed');

        if (button) button.textContent = 'Added';
        await refreshCartDrawer();
        openPanel('sq-cart-drawer');
      } catch (error) {
        if (button) button.textContent = 'Unavailable';
      } finally {
        setTimeout(() => {
          if (!button) return;
          button.disabled = false;
          button.textContent = original;
        }, 1400);
      }
    });
  }

  /* ---------- hero word reveal ---------- */
  function initHeroWords() {
    document.querySelectorAll('[data-sq-words]').forEach((el) => {
      if (el.dataset.sqWordsDone) return;
      const words = el.textContent.trim().split(/\s+/);
      el.innerHTML = words
        .map((word, i) => `<span class="sq-word" style="--w:${i}"><span>${word}</span></span>`)
        .join(' ');
      el.dataset.sqWordsDone = 'true';
    });
  }

  /* ---------- marquee seamless loop ---------- */
  function initMarquee() {
    document.querySelectorAll('[data-sq-marquee]').forEach((marquee) => {
      const track = marquee.querySelector('.sq-marquee__track');
      const group = track?.querySelector('.sq-marquee__group');
      if (!track || !group || track.children.length > 1) return;
      track.appendChild(group.cloneNode(true));
    });
  }

  function boot() {
    initReveal();
    initHeader();
    initAnnouncement();
    initPanels();
    initCarousels();
    initDensity();
    initVariantPickers();
    initCart();
    initHeroWords();
    initMarquee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
